import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import {
  EhrAppointmentCategory,
  EhrAppointmentKind,
  EhrAppointmentSchedulingRule,
  EhrAppointmentSchedulingRuleDef,
  FilterAppointmentsKind,
} from '@src/types'
import { EhrAppointmentsQuery } from '@src/screens/NutritionistHub/graphql/ehrAppointments.generated'
import { EHR_APPOINTMENTS } from '@src/screens/NutritionistHub/graphql/ehrAppointments'

const POLLING_INTERVAL = 3000 // 3 seconds
const TOTAL_POLLING_DURATION = 30 * 1000 // 30 seconds

interface PurchasedAppointmentParams {
  product: { ehrSchedulingRule: Pick<EhrAppointmentSchedulingRuleDef, 'key' | 'appointmentKind'> }
}

export const usePurchasedAppointment = ({ product }: PurchasedAppointmentParams) => {
  const [appointmentLoadingError, setAppointmentLoadingError] = useState(false)

  const isSchedulingRequired =
    product.ehrSchedulingRule.key !== EhrAppointmentSchedulingRule.NoScheduling

  const { data: pendingEhrAppointmentsData, stopPolling } = useQuery<EhrAppointmentsQuery>(
    EHR_APPOINTMENTS,
    {
      skip: !isSchedulingRequired,
      pollInterval: POLLING_INTERVAL,
      variables: {
        filterKind: FilterAppointmentsKind.PendingSchedule,
        category: EhrAppointmentCategory.Coaching,
      },
    },
  )

  const pendingAppointment = useMemo(() => {
    const allPendingEhrAppointments = pendingEhrAppointmentsData?.ehrAppointments.appointments || []

    return allPendingEhrAppointments.find(
      (appointment) => appointment.kind === product.ehrSchedulingRule.appointmentKind,
    )
  }, [pendingEhrAppointmentsData, product.ehrSchedulingRule.appointmentKind])

  // Set timeout for polling if we need to receive appointment to proceed
  useEffect(() => {
    if (!isSchedulingRequired) {
      return
    }
    if (pendingAppointment) {
      return
    }

    const timeoutHandle = setTimeout(() => {
      stopPolling()
      setAppointmentLoadingError(true)
    }, TOTAL_POLLING_DURATION)

    return () => {
      stopPolling()
      clearTimeout(timeoutHandle)
    }
  }, [pendingAppointment, isSchedulingRequired, stopPolling])

  const pendingAppointmentKind =
    pendingAppointment?.kind ??
    product.ehrSchedulingRule.appointmentKind ??
    EhrAppointmentKind.NoneCovered

  return {
    pendingAppointment,
    pendingAppointmentKind,
    appointmentLoadingError,
    isSchedulingRequired,
    stopPolling,
  }
}
